/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Search, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { listSpecialties, createPatientPremiumCheckout } from '@/lib/telemedicine';
import BookingWizard from '@/components/telemedicine/BookingWizard';
import { DoctorsGridBody, PremiumBanner, LimitsPill } from '@/components/telemedicine/marketplace/parts';
import { AppointmentMiniRow } from '@/components/telemedicine/marketplace/AppointmentMiniRow';

const PatientMarketplace = ({ config, limits, doctors, appointments, loading, onRefresh }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedSpec, setSelectedSpec] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [bookingDoctor, setBookingDoctor] = useState(null);
  const [premiumLoading, setPremiumLoading] = useState(false);

  useEffect(() => {
    listSpecialties()
      .then((r) => setSpecialties(r.specialties || []))
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') console.debug('[Marketplace] listSpecialties failed', err);
      });
  }, []);

  // Sprint 9H — Family bundle checkout return handling
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('family_checkout');
    if (outcome === 'success') {
      toast.success('Bundle famiglia attivato — grazie!');
      // Clean URL
      params.delete('family_checkout'); params.delete('session_id');
      const q = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
      onRefresh?.();
    } else if (outcome === 'cancel') {
      toast.info('Attivazione bundle annullata.');
      params.delete('family_checkout');
      const q = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
    }
  }, [onRefresh]);

  // Sprint 9H — Cinematic Hero deep-link: open booking wizard on custom event
  useEffect(() => {
    const handler = (e) => {
      const first = (doctors || []).find((d) => d && d.id);
      if (first) {
        setBookingDoctor(first);
        e.detail?.onHandled?.();
      }
    };
    document.addEventListener('sanitanexus:open-booking', handler);
    return () => document.removeEventListener('sanitanexus:open-booking', handler);
  }, [doctors]);

  const filtered = useMemo(() => {
    return doctors.filter((d) => {
      if (selectedSpec && d.specialty !== selectedSpec) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(d.full_name || '').toLowerCase().includes(q) && !(d.specialty || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [doctors, search, selectedSpec]);

  const upgrade = async () => {
    setPremiumLoading(true);
    try {
      const { url } = await createPatientPremiumCheckout(window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('genericError'));
      setPremiumLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="patient-marketplace">
      {/* Hero */}
      <div className="rounded-3xl bg-hero-gradient border border-border/60 p-6 md:p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display font-bold text-2xl md:text-3xl tracking-tight">{t('telemedTitle')}</h1>
              <p className="text-sm text-muted-foreground mt-1 max-w-lg">{t('telemedSubtitle')}</p>
            </div>
            {limits && <LimitsPill limits={limits} />}
          </div>
          {/* Search */}
          <div className="mt-4 relative max-w-xl">
            <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('telemedSearchPlaceholder')}
              className="h-12 pl-11 rounded-2xl shadow-elev-sm bg-card/90"
              data-testid="doctor-search-input"
            />
          </div>
        </div>
      </div>

      {/* Premium banner (if not premium) */}
      <PremiumBanner limits={limits} config={config} onUpgrade={upgrade} loading={premiumLoading} />

      {/* Specialty filter chips */}
      {specialties.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin -mx-1 px-1" data-testid="specialty-filter-row">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase font-bold text-muted-foreground flex-shrink-0 mr-1">
            <Filter className="h-3 w-3" /> {t('telemedFilterBySpec')}
          </div>
          <button
            onClick={() => setSelectedSpec(null)}
            className={cn(
              'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border',
              !selectedSpec ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border/60 text-foreground hover:border-primary/40'
            )}
            data-testid="specialty-chip-all"
          >
            {t('telemedAllSpecs')}
          </button>
          {specialties.map((s) => (
            <button
              key={s}
              onClick={() => setSelectedSpec(s)}
              className={cn(
                'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border capitalize',
                selectedSpec === s ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border/60 text-foreground hover:border-primary/40'
              )}
              data-testid={`specialty-chip-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Doctors grid */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-semibold text-lg">{t('telemedDoctorsListTitle')}</h2>
          <span className="text-xs text-muted-foreground">{filtered.length} {t('telemedActiveDoctors')}</span>
        </div>
        <DoctorsGridBody loading={loading} filtered={filtered} limits={limits} onBook={setBookingDoctor} emptyLabel={t('telemedNoDoctorsMatch')} />
      </div>

      {/* My appointments */}
      <div>
        <h2 className="font-display font-semibold text-lg mb-3">{t('telemedMyAppointments')}</h2>
        {appointments.length === 0 ? (
          <Card className="p-8 text-center" data-testid="no-appointments-empty">
            <p className="text-sm text-muted-foreground">{t('telemedNoAppointments')}</p>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="appointments-list">
            {appointments.map((a) => (
              <AppointmentMiniRow key={a.id} a={a} onOpenRoom={(id) => navigate(`/telemedicine/room/${id}`)} />
            ))}
          </div>
        )}
      </div>

      <BookingWizard
        doctor={bookingDoctor}
        limits={limits}
        onClose={() => setBookingDoctor(null)}
        onBooked={onRefresh}
      />
    </div>
  );
};

export default PatientMarketplace;
